import { useQuery, useQueryClient } from '@tanstack/react-query'
import { baseURL, headers, requestMiddleware } from '@/v2/constants/requests'

export const useContracts = (patientId, options = {}) => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['Contracts', `${patientId}`],
    queryFn: async () => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/documents/contracts.json`
      )
      if (!res.ok) {
        throw new Error("We couldn't find the contracts")
      }
      const result = await res.json()

      return result
    },
    enabled: !!patientId,
    ...options,
  })

  return { contracts: data, isLoading, isError, refetch }
}

export const useCreateContract = patientId => {
  const queryClient = useQueryClient()
  const { mutateAsync: createContract, ...others } = requestMiddleware({
    key: ['CreateContract', patientId],
    request: async values =>
      await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/documents/contracts.json`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            down_payment: values.downPayment,
            description: values.description,
            total_cost: values.totalCost,
            interest_rate: values.interestRate,
            discount_rate: values.discountRate,
            processing_fee_percentage: values.processingFeePercentage,
            monthly_payment_count: values.monthlyPaymentCount + 1,
            monthly_payment_amount: values.monthlyPaymentAmount,
            // Flat and snake_case on purpose: wrap_parameters only folds keys
            // that match a Contract column, so a camelCase key is dropped
            // silently and the contract defaults to Invisalign.
            treatment_type: values.treatmentType,
          }),
        }
      ),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['Contracts', `${patientId}`],
      })
      queryClient.invalidateQueries({
        queryKey: ['PatientProfile', `${patientId}`],
      })
    },
  })

  return { createContract, ...others }
}

export const useArchiveContract = (patientId, contractId) => {
  const queryClient = useQueryClient()
  const { mutateAsync: archiveContract, ...others } = requestMiddleware({
    key: ['ArchiveContract', patientId, contractId],
    request: async () =>
      await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/documents/contracts/${contractId}.json`,
        {
          method: 'DELETE',
          headers,
        }
      ),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['Contracts', `${patientId}`],
      })
      queryClient.invalidateQueries({
        queryKey: ['PatientProfile', `${patientId}`],
      })
    },
  })

  return { archiveContract, ...others }
}

export const useUpdateContractTreatmentType = (patientId, contractId) => {
  const queryClient = useQueryClient()
  const { mutateAsync: updateTreatmentType, ...others } = requestMiddleware({
    key: ['UpdateContractTreatmentType', patientId, contractId],
    request: async treatmentType =>
      await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/documents/contracts/${contractId}/treatment_type.json`,
        {
          method: 'PATCH',
          headers,
          // Wrapped, unlike create: this endpoint calls require(:contract).
          body: JSON.stringify({
            contract: { treatment_type: treatmentType },
          }),
        }
      ),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['Contracts', `${patientId}`],
      })
      queryClient.invalidateQueries({
        queryKey: ['PatientProfile', `${patientId}`],
      })
    },
  })

  return { updateTreatmentType, ...others }
}

export const useSendContract = contractId => {
  const { mutateAsync: sendContract, ...others } = requestMiddleware({
    key: ['SendContract', contractId],
    request: async () =>
      await fetch(`${baseURL}/v2/rest/contracts/${contractId}/emails`, {
        method: 'POST',
        headers,
      }),
  })

  return { sendContract, ...others }
}
